<template>
  <div :class="['newCalendarAgenda', 'card', type === 'agenda' ? 'agenda' : '']">

    <div class="header">
      <div class="title">
        <span class="text-heading">Kalender Agenda</span>
      </div>

      <div class="month-nav">
        <button class="nav-btn" type="button" @click="shiftWeek(-1)" aria-label="Prev week">
          <img alt="" src="../../assets/icons/arrow_left.svg" />
        </button>

        <div class="month-title" role="button" tabindex="0" @click="openPicker" @keydown.enter="openPicker">
          {{ monthLabel }}
        </div>

        <button class="nav-btn" type="button" @click="shiftWeek(1)" aria-label="Next week">
          <img alt="" src="../../assets/icons/arrow_right.svg" />
        </button>
      </div>
    </div>

    <div class="day-row">
      <button
        v-for="d in weekDays"
        :key="d.key"
        type="button"
        class="day-chip"
        :class="{ active: isSameDay(d.date, selectedDate) }"
        @click="pickDay(d.date)"
      >
        <div class="dow">{{ d.dow }}</div>
        <div class="dom">{{ d.dom }}</div>

        <div v-if="hasDot(d.date)" class="dot"></div>
      </button>
    </div>

    <!-- List agenda -->
    <div class="agenda-wrap">
      <div v-if="loading" class="state">Memuat...</div>

      <template v-else>
        <div v-if="!safeList.length" class="state">
          Belum ada agenda
        </div>

        <div v-else class="agenda-list">
          <div
            v-for="agenda in safeList"
            :key="agenda.slug || agenda.event_id"
            class="agenda-card"
            role="button"
            tabindex="0"
            @click="onEventClick(agenda)"
          >
            <div class="agenda-strip"></div>

            <div class="agenda-body">
              <div class="agenda-date">
                {{ formatHeaderDate(selectedDate) }}
              </div>

              <div class="agenda-title">
                {{ agenda.title }}
              </div>

              <div class="agenda-divider"></div>

              <div class="agenda-meta">
                <div class="meta-row">
                  <img src="@/assets/icons/clock.svg" class="meta-icon" />
                  <span>{{ formatTimeRange(agenda.event_start_date, agenda.event_end_date) }}</span>
                </div>

                <div v-if="agenda.location" class="meta-row">
                  <img src="@/assets/icons/pin.svg" class="meta-icon" />
                  <span>{{ agenda.location }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Modal DatePicker (opsional, dibuka saat klik judul bulan) -->
    <div class="picker-modal" v-if="showPicker">
      <div class="picker-card">
        <DatePicker
          title-position="left"
          :first-day-of-week="2"
          v-model="pickerDate"
          :masks="{ title: 'MMM YYYY', weekdays: 'WW' }"
          :attributes="attrs"
          class="picker"
        />
        <button class="btn-fill blue text-bold rounded" @click="applyPicker">
          Pilih Tanggal
        </button>
      </div>

      <div class="overlay" @click="closePicker"></div>
    </div>
  </div>
</template>

<script>
import { DatePicker } from 'v-calendar'

export default {
  name: 'NewAgenda',
  components: { DatePicker },

  props: {
    type: { type: String, default: 'home' },
    list: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },

  data () {
    return {
      selectedDate: new Date(),

      // modal picker
      showPicker: false,
      pickerDate: new Date(),

      // dot untuk v-calendar (opsional)
      attrs: [{
        dot: { style: { backgroundColor: '#ed1c24' }, class: 'customDot' },
        dates: new Date()
      }]
    }
  },

  computed: {
    safeList () {
      return Array.isArray(this.list) ? this.list : []
    },

    monthLabel () {
      const d = this.selectedDate
      const months = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
      ]
      return `${months[d.getMonth()]} ${d.getFullYear()}`
    },

    weekDays () {
      const base = this.startOfWeek(this.selectedDate) // Senin
      const dows = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min']

      return Array.from({ length: 7 }).map((_, i) => {
        const date = new Date(base)
        date.setDate(base.getDate() + i)
        return {
          key: this.toYMD(date),
          date,
          dow: dows[i],
          dom: date.getDate()
        }
      })
    },
    groupedAgendas () {
    const items = [...this.safeList]
      .filter(it => it?.event_start_date)
      .map(it => ({
        ...it,
        __start: new Date(it.event_start_date)
      }))
      .filter(it => !isNaN(it.__start.getTime()))
      .sort((a, b) => a.__start.getTime() - b.__start.getTime())

    const map = new Map()

    for (const it of items) {
      const key = this.toYMD(it.__start)
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(it)
    }

    return Array.from(map.entries()).map(([key, list]) => {
      const d = new Date(list[0].event_start_date)
      return {
        key,
        label: this.formatHeaderDate(d), // "5 Jan 2026"
        items: list
      }
    })
  }
  },

  mounted () {
    // emit awal supaya parent fetch hari ini (kalau mau otomatis)
    this.$emit('dateselected', this.selectedDate)
  },

  methods: {
    pickDay (date) {
      this.selectedDate = new Date(date)
      this.$emit('dateselected', this.selectedDate)
    },

    shiftWeek (dir) {
      const next = new Date(this.selectedDate)
      next.setDate(next.getDate() + dir * 7)
      this.selectedDate = next
      // optional: auto fetch saat geser minggu (bisa kamu matikan kalau tidak mau)
      this.$emit('dateselected', this.selectedDate)
    },

    onEventClick (agenda) {
      this.$emit('eventselected', agenda)
    },

    ymdFromIso (iso) {
      if (!iso) return null
      const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/)
      return m ? m[1] : null
    },

    hasDot (date) {
      const key = this.toYMD(date)

      return this.safeList.some(ev => {
        const startKey = this.ymdFromIso(ev?.event_start_date)
        if (!startKey) return false

        const endKey = this.ymdFromIso(ev?.event_end_date) || startKey

        return key >= startKey && key <= endKey
      })
    },

    openPicker () {
      this.pickerDate = new Date(this.selectedDate)
      this.showPicker = true
    },
    closePicker () {
      this.showPicker = false
    },
    applyPicker () {
      this.selectedDate = new Date(this.pickerDate)
      this.showPicker = false
      this.$emit('dateselected', this.selectedDate)
    },

    // ====== utils ======
    toYMD (date) {
      const y = date.getFullYear()
      const m = date.getMonth() + 1
      const d = date.getDate()
      const mm = m < 10 ? `0${m}` : `${m}`
      const dd = d < 10 ? `0${d}` : `${d}`
      return `${y}-${mm}-${dd}`
    },

    startOfWeek (date) {
      const d = new Date(date)
      const day = d.getDay() // 0=Min..6=Sab
      const diff = (day === 0 ? -6 : 1) - day // ke Senin
      d.setDate(d.getDate() + diff)
      d.setHours(0, 0, 0, 0)
      return d
    },

    isSameDay (a, b) {
      return a && b && a.toDateString() === b.toDateString()
    },

    shortMonthName (date) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
      return months[date.getMonth()]
    },

    formatHeaderDate (date) {
      const d = new Date(date)
      const day = d.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
      return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`
    },

    formatTime (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${hh}:${mm}`
    },

    shouldShowTime (agenda) {
      const s = this.formatTime(agenda?.event_start_date)
      const e = this.formatTime(agenda?.event_end_date)

      const meaningful = (t) => t && t !== '00:00'
      return meaningful(s) || meaningful(e)
    },

    getLocation (agenda) {
      return agenda?.location || agenda?.place || agenda?.venue || ''
    },

    formatDateRange (startIso, endIso) {
      if (!startIso || !endIso) return ''
      const s = new Date(startIso)
      const e = new Date(endIso)
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return ''

      const sameDay = this.toYMD(s) === this.toYMD(e)
      if (sameDay) return ''

      return `${this.formatHeaderDate(s)} - ${this.formatHeaderDate(e)}`
    },
     formatTime (iso) {
      const d = new Date(iso)
      const hh = String(d.getHours()).padStart(2,'0')
      const mm = String(d.getMinutes()).padStart(2,'0')
      return `${hh}:${mm}`
    },

    formatTimeRange (start, end) {
      return `${this.formatTime(start)} - ${this.formatTime(end)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.newCalendarAgenda {
  border-radius: 14px;
  padding: 14px;
  background: #fff;
  width: 100%;

  @media only screen and (max-width: 600px) {
    padding: 14px 12px;
  }
}

.header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.month-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.month-title {
  text-align: center;
  font-weight: 800;
  font-size: 18px;
  color: #1f2a55;
  cursor: pointer;
  user-select: none;
  margin-right: 8px;
  margin-left: 8px;
}

.nav-btn {
  height: 36px;
  width: 36px;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  color: #1f4db8;
}

.day-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 14px;
}

.day-chip {
  width: 100%;
  height: 84px;
  border-radius: 14px;
  border: 1.5px solid #e7ecff;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 8px;
  gap: 6px;
  cursor: pointer;
}

.day-chip.active {
  border-color: #1f4db8;
}

.dow { font-weight: 700; color: #1f4db8; font-size: 14px; }
.dom { font-weight: 800; color: #1f2a55; font-size: 18px; line-height: 1; }

.dot {
  width: 10px;
  height: 10px;
  background: #ed1c24;
  border-radius: 999px;
  margin-top: 6px;
}

/* list */
.agendaCard {
  display: grid;
  grid-template-columns: 1fr 6fr;
  background: #f0f4ff;
  padding: 10px;
  border-radius: 10px;
  align-items: center;
  grid-gap: 1rem;
  cursor: pointer;
}

.titleAgenda {
  font-weight: 800;
  color: #374151;
  margin-bottom: 10px;
}

.state {
  padding: 10px;
  color: #6b7280;
}

/* modal picker */
.picker-modal {
  position: fixed;
  inset: 0;
  z-index: 999;
}

.picker-card {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 340px;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  z-index: 1000;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,.35);
}

.title {
  font-weight: 800;
  color: #374151;
}

.agenda-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.agenda-card {
  display: grid;
  grid-template-columns: 8px 1fr;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e7ecff;
  overflow: hidden;
  margin-bottom: 12px;
  cursor: pointer;
}

.agenda-strip {
  background: #465F97;
}

.agenda-body {
  padding: 14px;
}

.agenda-date {
  font-weight: 800;
  color: #2f4fa3;
  font-size: 16px;
  margin-bottom: 6px;
}

.agenda-title {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  line-height: 1.3;
}

.agenda-divider {
  height: 1px;
  background: #e7ecff;
  margin: 10px 0;
}

.agenda-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
}

.meta-icon {
  width: 18px;
  height: 18px;
}

.agenda-wrap {
  max-height: 216px;
  overflow: auto;
}
</style>

